import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const LeadForm = () => {
  const [address, setAddress] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (address.trim()) {
      navigate(`/get-offer?address=${encodeURIComponent(address)}`);
    } else {
      navigate('/get-offer');
    }
  };

  const handleAddressSelect = (place) => {
    // Navigate directly with the selected address from Google Places
    if (place && place.formatted_address) {
      navigate(`/get-offer?address=${encodeURIComponent(place.formatted_address)}`);
    }
  };

  return (
    <section id="get-offer" className="py-24 md:py-32 bg-slate-50" data-testid="lead-form-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-sm font-semibold text-[#D32F2F] uppercase tracking-wide mb-4">
              Get Started Today
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Get Your Free
              <br />
              <span className="text-[#D32F2F]">Cash Offer</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Enter your property address and we'll get back to you within 24 hours with a fair, no-obligation cash offer.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D32F2F]/10 rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#D32F2F]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Call Us Anytime</p>
                  <a href="tel:626-526-1420" className="text-lg font-semibold text-slate-900 hover:text-[#D32F2F]">
                    626-526-1420
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D32F2F]/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#D32F2F]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email Us</p>
                  <a href="mailto:Sell@JJbuyshousesforcash.com" className="text-lg font-semibold text-slate-900 hover:text-[#D32F2F]">
                    Sell@JJbuyshousesforcash.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Address Search Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
              <h3 
                className="text-2xl font-bold text-slate-900 mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Start Your Cash Offer
              </h3>
              <p className="text-slate-600 mb-6">
                Enter your property address to get started
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="section-address-form">
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onSelect={handleAddressSelect}
                  placeholder="Enter your property address..."
                  inputClassName="h-14 text-lg border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20 rounded-xl"
                  testId="section-address-input"
                />
                
                <Button
                  type="submit"
                  className="w-full h-14 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all btn-primary"
                  data-testid="section-submit-btn"
                >
                  Get My Cash Offer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>

              <p className="text-xs text-slate-500 text-center mt-4">
                No obligation • 100% Free • Fast response
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
